// Contrat commun du lot L2 entre les modules qui font naître une créance (marchés municipaux, domaine
// public, démarches payantes, réservations) et le module des recettes qui la tarife et l'émet
// (H4-03, H4-04). Montants en francs CFA (XAF), entiers, arrondis au franc supérieur par le module
// des recettes. Un module métier ne calcule jamais un montant lui-même : il demande la tarification,
// l'affiche, puis demande l'émission dans la transaction de l'acte qui fait naître la créance.
import { z } from 'zod';

export const NATURES_FAIT_GENERATEUR = [
  'attribution_emplacement',
  'autorisation_domaine_public',
  'demarche_payante',
  'reservation_equipement',
  'avis_imposition',
] as const;
export type NatureFaitGenerateur = (typeof NATURES_FAIT_GENERATEUR)[number];

const jour = z.iso.date('Date AAAA-MM-JJ attendue');

/** Base de calcul fournie par le module métier ; le tarif de la rubrique dit laquelle est utilisée. */
export const schemaBaseTarification = z.object({
  surface_m2: z.number().positive().optional(),
  quantite: z.number().int().positive().optional(),
  jours: z.number().int().positive().optional(),
  /** Pour une base en pourcentage : montant de référence déclaré. */
  montant_reference: z.number().int().nonnegative().optional(),
});
export type BaseTarification = z.infer<typeof schemaBaseTarification>;

export const schemaDemandeTarification = z.object({
  rubrique_code: z.string().regex(/^[a-z][a-z0-9_]{1,62}$/),
  base: schemaBaseTarification,
  periode: z.object({ debut: jour, fin: jour }),
});
export type DemandeTarification = z.infer<typeof schemaDemandeTarification>;

/** Résultat d'une tarification : le montant et la règle lisible qui l'explique (affichée à l'agent et à l'usager). */
export const schemaTarification = z.object({
  rubrique_id: z.string().uuid(),
  tarif_id: z.string().uuid(),
  montant: z.number().int().nonnegative(),
  devise: z.literal('XAF'),
  /** Exemple : « 6 m² × 1 500 F par m² et par mois × 3 mois = 27 000 F ». */
  regle: z.string(),
  /** Tarif marqué fictif (données de présentation) : suffixe « (fictif) » à l'affichage (HD-02). */
  fictif: z.boolean(),
});
export type Tarification = z.infer<typeof schemaTarification>;

/** Redevable désigné par le module métier ; le module des recettes retrouve ou crée la fiche redevable. */
export const schemaRedevableDesigne = z
  .object({
    usager_compte_id: z.string().uuid().optional(),
    nom: z.string().min(1).max(200).optional(),
    type: z.enum(['personne', 'entreprise']).default('personne'),
    contact: z.record(z.string(), z.string()).optional(),
  })
  .refine((r) => r.usager_compte_id || r.nom, { message: 'Indiquer le compte usager ou le nom du redevable' });

export const schemaFaitGenerateur = z.object({
  nature: z.enum(NATURES_FAIT_GENERATEUR),
  /** Objet métier à l'origine de la créance (attribution, autorisation, dossier, réservation). */
  objet_type: z.string().regex(/^[a-z][a-z0-9_]{1,62}$/),
  objet_id: z.string().uuid(),
  redevable: schemaRedevableDesigne,
  demande: schemaDemandeTarification,
  echeance: jour,
  libelle: z.string().min(1).max(300),
});
export type FaitGenerateur = z.infer<typeof schemaFaitGenerateur>;

export const schemaCreanceEmise = z.object({
  creance_id: z.string().uuid(),
  numero: z.string(),
  montant_du: z.number().int().nonnegative(),
  regle: z.string(),
});
export type CreanceEmise = z.infer<typeof schemaCreanceEmise>;
